import { TuiNotificationService } from '@taiga-ui/core';
import { inject, Injectable } from '@angular/core';
import { Auth, signInWithCustomToken } from '@angular/fire/auth';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, from, Observable, of, switchMap, tap } from 'rxjs';
import { isRtpTokenExpired } from 'src/app/shared/utils/token-utils';
import { AuthenticationCredentialsService } from '@modules/authentication/services/authentication-credentials.service';
import { AuthenticationApiService } from '@modules/authentication/api/authentication.api.service';

@UntilDestroy()
@Injectable()
export class FirebaseAuthenticationService {
  private authenticationApiService = inject(AuthenticationApiService);
  private authenticationCredentialsService = inject(AuthenticationCredentialsService);
  private alertService = inject(TuiNotificationService);
  private auth = inject(Auth);
  private translocoService = inject(TranslocoService);

  authenticate(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if (!this.auth.currentUser) {
        this.signIn()
          .pipe(untilDestroyed(this))
          .subscribe((isAuthenticated) => {
            if (!isAuthenticated) {
              this.onRtpTokenError();
            }
            observer.next(isAuthenticated);
            observer.complete();
          });
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }

  signIn(): Observable<boolean> {
    return this.getRtpToken().pipe(
      switchMap((rtpToken: string) =>
        from(
          signInWithCustomToken(this.auth, rtpToken)
            .then(() => true)
            .catch(() => false),
        ),
      ),
      catchError(() => of(false)),
    );
  }

  getRtpToken(): Observable<string> {
    const rtpToken = this.authenticationCredentialsService.getRtpToken();
    if (rtpToken && !isRtpTokenExpired(rtpToken)) {
      return of(rtpToken);
    } else {
      return this.authenticationApiService
        .refreshRtpToken()
        .pipe(tap((newRtpToken: string) => this.authenticationCredentialsService.setRtpToken(newRtpToken)));
    }
  }

  private onRtpTokenError(): void {
    this.alertService
      .open(this.translocoService.translate('Une erreur est survenue, merci de réessayer dans quelques instants'), {
        label: this.translocoService.translate('Erreur du serveur'),
        appearance: 'negative',
      })
      .subscribe();
  }
}
